'use client';

import { invertTheme, Theme } from '@/lib/util/theme';
import { useTheme } from '@/providers/theme-provider';
import { LogoProps } from '@/ui/Logo';
import { useEffect, useMemo, useState } from 'react';
import { NavBarStyle, ScrollState } from './types';

const makeScrollHandler =
  (scrollState: ScrollState, setScrollState: (state: ScrollState) => void) => () => {
    const isScrolled = window.scrollY > 10;
    const newScrollState = isScrolled ? 'scrolled' : 'top';
    if (newScrollState !== scrollState) setScrollState(newScrollState);
  };

export function useScrollState() {
  const [scrollState, setScrollState] = useState<ScrollState>('top');
  useEffect(() => {
    const handleScroll = makeScrollHandler(scrollState, setScrollState);

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrollState]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const handleScroll = makeScrollHandler('top', setScrollState);
      handleScroll();
    }
  }, []);

  return scrollState;
}

export function useAnimation(
  scrollState: ScrollState,
  topStyle: NavBarStyle,
  scrolledStyle: NavBarStyle,
  mobileMenuOpen?: boolean
) {
  const { theme } = useTheme();
  const style = useMemo<NavBarStyle>(
    () => (scrollState === 'top' && !mobileMenuOpen ? topStyle : scrolledStyle),
    [scrollState, topStyle, scrolledStyle, mobileMenuOpen]
  );
  const inverted = useMemo<boolean>(
    () => style === 'transparentInverted' || style === 'opaqueInverted',
    [style]
  );
  const logoVariant = useMemo<LogoProps['variant']>(
    () => (theme === 'dark' || (theme === 'light' && inverted) ? 'dark' : 'light'),
    [theme, inverted]
  );
  const variant = useMemo<'transparent' | 'opaque'>(
    () => (style === 'transparent' || style === 'transparentInverted' ? 'transparent' : 'opaque'),
    [style]
  );
  const navBarTheme = useMemo<Theme>(
    () => (inverted ? invertTheme(theme) : theme),
    [inverted, theme]
  );
  const transparent = useMemo<string>(
    () =>
      (navBarTheme === 'light' && !inverted) || (navBarTheme === 'dark' && inverted)
        ? 'rgba(255, 255, 255, 0)'
        : 'rgba(0, 0, 0, 0)',
    [navBarTheme, inverted]
  );

  return { variant, navBarTheme, logoVariant, transparent };
}
